import { isPointInPolygon } from '@data/geometry/Functions';
import FVector from '@data/geometry/FVector';
import * as THREE from 'three';

export type PolygonLoop = FVector[];
export type PolygonRings = PolygonLoop[];
export type MultiPolygonRings = PolygonRings[];

type IndexedLoop = {
    area: number;
    index: number;
    loop: PolygonLoop;
};

export class PolygonShapeBuilder {
    static buildPolygonsFromLoops(loops: PolygonLoop[]): MultiPolygonRings {
        const normalizedLoops = loops.map((loop) => PolygonShapeBuilder.normalizeLoop(loop)).filter((loop) => loop.length >= 4);
        if (!normalizedLoops.length) return [];

        const indexedLoops: IndexedLoop[] = normalizedLoops
            .map((loop, index) => ({ loop, index, area: PolygonShapeBuilder.signedArea2D(loop) }))
            .sort((a, b) => Math.abs(b.area) - Math.abs(a.area));

        const parentByIndex = new Map<number, number | undefined>();
        for (const current of indexedLoops) {
            const samplePoint = current.loop[0];
            const parent = indexedLoops.find(
                (candidate) =>
                    candidate.index !== current.index &&
                    Math.abs(candidate.area) > Math.abs(current.area) &&
                    isPointInPolygon(samplePoint, PolygonShapeBuilder.openLoop(candidate.loop))
            );
            parentByIndex.set(current.index, parent?.index);
        }

        const depthByIndex = new Map<number, number>();
        const getDepth = (index: number): number => {
            if (depthByIndex.has(index)) return depthByIndex.get(index)!;
            const parentIndex = parentByIndex.get(index);
            const depth = parentIndex === undefined ? 0 : getDepth(parentIndex) + 1;
            depthByIndex.set(index, depth);
            return depth;
        };

        const outerLoops = indexedLoops.filter((entry) => getDepth(entry.index) % 2 === 0);
        return outerLoops.map((outer) => {
            const polygon: PolygonRings = [PolygonShapeBuilder.ensureCounterClockwise(outer.loop)];

            const immediateHoles = indexedLoops.filter(
                (candidate) => parentByIndex.get(candidate.index) === outer.index && getDepth(candidate.index) % 2 === 1
            );
            for (const hole of immediateHoles) {
                polygon.push(PolygonShapeBuilder.ensureClockwise(hole.loop));
            }

            return polygon;
        });
    }

    static createShapesFromLoops(loops: PolygonLoop[]): THREE.Shape[] {
        return PolygonShapeBuilder.createShapesFromPolygons(PolygonShapeBuilder.buildPolygonsFromLoops(loops));
    }

    static createShapesFromPolygons(polygons: MultiPolygonRings): THREE.Shape[] {
        const shapes: THREE.Shape[] = [];

        for (const polygon of polygons) {
            if (!polygon.length) continue;

            const outerLoop = PolygonShapeBuilder.ensureCounterClockwise(PolygonShapeBuilder.normalizeLoop(polygon[0]));
            if (outerLoop.length < 4) continue;

            const shape = new THREE.Shape(PolygonShapeBuilder.toVector2Loop(outerLoop));
            for (let i = 1; i < polygon.length; i++) {
                const holeLoop = PolygonShapeBuilder.ensureClockwise(PolygonShapeBuilder.normalizeLoop(polygon[i]));
                if (holeLoop.length < 4) continue;
                shape.holes.push(new THREE.Path(PolygonShapeBuilder.toVector2Loop(holeLoop)));
            }

            shapes.push(shape);
        }

        return shapes;
    }

    static normalizeLoop(loop: PolygonLoop): PolygonLoop {
        if (!loop.length) return [];

        const normalized = loop.map((point) => new FVector(point.x, point.y, point.z));
        const first = normalized[0];
        const last = normalized[normalized.length - 1];
        if (first.x !== last.x || first.y !== last.y) normalized.push(new FVector(first.x, first.y, first.z));
        return normalized;
    }

    static openLoop(loop: PolygonLoop): PolygonLoop {
        if (loop.length < 2) return loop;
        const first = loop[0];
        const last = loop[loop.length - 1];
        if (first.x === last.x && first.y === last.y) return loop.slice(0, -1);
        return loop;
    }

    private static ensureCounterClockwise(loop: PolygonLoop): PolygonLoop {
        return PolygonShapeBuilder.signedArea2D(loop) < 0 ? loop.slice().reverse() : loop;
    }

    private static ensureClockwise(loop: PolygonLoop): PolygonLoop {
        return PolygonShapeBuilder.signedArea2D(loop) > 0 ? loop.slice().reverse() : loop;
    }

    private static signedArea2D(loop: PolygonLoop): number {
        let area = 0;
        for (let i = 0; i < loop.length - 1; i++) {
            const a = loop[i];
            const b = loop[i + 1];
            area += a.x * b.y - b.x * a.y;
        }
        return area * 0.5;
    }

    private static toVector2Loop(loop: PolygonLoop): THREE.Vector2[] {
        return PolygonShapeBuilder.openLoop(loop).map((point) => new THREE.Vector2(point.x, point.y));
    }
}
